// Leveranse-lenke — public, mobile-first page where the producer answers.
// Reached at /leveranse/<token>, no BMAX login.
//
// The hash is the credential, so this page never takes an order id and the
// backend only ever returns the producer-safe allowlist (no prices, no
// customer e-mail). See backend/src/services/vendorLink.js.
//
// TO VERSJONER, ÉN SIDE. `data.mode` avgjør hvilken:
//
//   "bestilling"  — kunden har bestilt og betalt gjennom Betongsentrum.
//                   Produsenten bekrefter med dato og tidspunkt, eller avslår.
//   "foresporsel" — samme henvendelse går til flere produsenter. Hver kan gi
//                   fastpris, overta kunden eksklusivt mot formidlingsgebyr,
//                   eller avslå. (backend/src/services/vendorRequest.js)
//
// Sidene deler alt over beslutningen — byggeplass, leveringsdetaljer,
// leveringsplan, leveringstid — og skiller lag i handlingene og i
// kundeinformasjonen nederst.
//
// Layouten følger kundens skisser (09.09.2026 og Utvikling | BMAx s. 2-3): én
// kolonne, kort etter kort, handlingene i flyten og ikke i en fastlåst
// bunnlinje. «Leveringsplan» er slått sammen til en seksjon man klikker opp —
// med seks lass fyller den ellers hele skjermen før man kommer til det man
// skal svare på.
//
// Kundens navn, telefon og e-post finnes ikke i nyttelasten i forespørselsmodus
// før produsenten har overtatt; det er backend som holder dem tilbake, ikke
// dette UI-et. `customer_unlocked` er flagget vi tegner etter.
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";

import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ArticleIcon from "@mui/icons-material/Article";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import ScienceIcon from "@mui/icons-material/Science";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AdjustIcon from "@mui/icons-material/Adjust";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PersonIcon from "@mui/icons-material/Person";
import ApartmentIcon from "@mui/icons-material/Apartment";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";

import "./style.css";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

/* ------------------------------ design tokens ----------------------------- */
const BRAND = "#1d4ed8";   // primary actions and accents
const GREEN = "#0f8a43";   // reserved for confirmed states only
const RED = "#c0392b";
const AMBER = "#c2410c";   // forespørsel — verken bekreftelse eller avslag
const INK = "#111c2d";
const MUTED = "#67748e";
const LINE = "#e6ecf3";
const TINT = "#eff5fd";    // leveringsdetaljer-kortet i skissen

const S = {
  label: {
    fontSize: 10.5,
    letterSpacing: ".05em",
    textTransform: "uppercase",
    color: MUTED,
    margin: 0,
  },
  value: { fontSize: 15, fontWeight: 700, margin: "2px 0 0", wordBreak: "break-word" },
  input: {
    width: "100%",
    minHeight: 48,
    padding: "10px 12px",
    fontSize: 16, // >=16px stops iOS Safari zooming the page on focus
    fontFamily: "inherit",
    border: `1px solid ${LINE}`,
    borderRadius: 12,
    boxSizing: "border-box",
    background: "#fff",
    color: INK,
  },
  btn: {
    width: "100%",
    minHeight: 54,
    border: "none",
    borderRadius: 14,
    fontSize: 16,
    fontWeight: 800,
    cursor: "pointer",
    letterSpacing: ".02em",
    fontFamily: "inherit",
  },
};

const NB_MONTHS = ["januar","februar","mars","april","mai","juni",
  "juli","august","september","oktober","november","desember"];

/** "24. juli 2025 kl. 14:32" — when the order reached the producer. */
const fmtReceived = (iso) => {
  if (!iso) return null;
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return null;
  const hh = String(dt.getHours()).padStart(2, "0");
  const mm = String(dt.getMinutes()).padStart(2, "0");
  return `${dt.getDate()}. ${NB_MONTHS[dt.getMonth()]} ${dt.getFullYear()} kl. ${hh}:${mm}`;
};

const NB_DAYS = ["søndag","mandag","tirsdag","onsdag","torsdag","fredag","lørdag"];

/** "2026-09-14" → "Mandag 14. september". Året er underforstått — leveransen
 *  ligger alltid innenfor 180 dager. */
const fmtLongDate = (iso) => {
  if (!iso) return "";
  const dt = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return iso;
  const day = NB_DAYS[dt.getDay()];
  return `${day.charAt(0).toUpperCase()}${day.slice(1)} ${dt.getDate()}. ${NB_MONTHS[dt.getMonth()]}`;
};

/* -------------------------------- byggeklosser ---------------------------- */
/** Seksjonsoverskrift: lite ikon + versal etikett, som i skissen. */
function Head({ icon: Ico, children, color }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
      {Ico && <Ico sx={{ fontSize: 17, color }} />}
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: ".08em",
          textTransform: "uppercase",
          color,
        }}
      >
        {children}
      </span>
    </div>
  );
}

Head.propTypes = {
  icon: PropTypes.elementType,
  children: PropTypes.node.isRequired,
  color: PropTypes.string,
};

Head.defaultProps = { icon: null, color: BRAND };

/** Ett felt i leveringsdetaljene: ikon til venstre, etikett over verdi. */
function Field({ icon: Ico, label, value }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      <Ico sx={{ fontSize: 20, color: BRAND, mt: "2px", flex: "0 0 auto" }} />
      <div style={{ minWidth: 0 }}>
        <p style={S.label}>{label}</p>
        <p style={S.value}>{value || "—"}</p>
      </div>
    </div>
  );
}

Field.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

Field.defaultProps = { value: null };

function Row({ label, value }) {
  return (
    <div>
      <p style={S.label}>{label}</p>
      <p style={S.value}>{value || "—"}</p>
    </div>
  );
}

Row.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node };
Row.defaultProps = { value: null };

/** Kontaktlinje med ikon — telefon og e-post i kundeinformasjonskortet. */
function ContactLinks({ phone, email }) {
  const style = {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    color: BRAND,
    fontWeight: 700,
    fontSize: 15,
    textDecoration: "none",
  };
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 18, marginTop: 12 }}>
      {phone && (
        <a href={`tel:${String(phone).replace(/\s/g, "")}`} style={style}>
          <PhoneIcon sx={{ fontSize: 17 }} />
          {phone}
        </a>
      )}
      {email && (
        <a href={`mailto:${email}`} style={style}>
          <EmailIcon sx={{ fontSize: 17 }} />
          {email}
        </a>
      )}
    </div>
  );
}

ContactLinks.propTypes = { phone: PropTypes.string, email: PropTypes.string };
ContactLinks.defaultProps = { phone: null, email: null };

/**
 * Handlingsknapp slik skissen tegner den: ikon, tittel med forklaring under,
 * og en chevron som sier at det skjer noe når man trykker.
 */
const TONES = {
  confirm: { bg: GREEN, fg: "#fff", border: GREEN, icon: "#fff" },
  primary: { bg: BRAND, fg: "#fff", border: BRAND, icon: "#fff" },
  takeover: { bg: "#fff7ed", fg: AMBER, border: "#fbd7b5", icon: AMBER },
  danger: { bg: "#fef4f3", fg: RED, border: "#f5c6c2", icon: RED },
};

function Action({ icon: Ico, title, subtitle, tone, onClick, disabled }) {
  const t = TONES[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        ...S.btn,
        display: "flex",
        alignItems: "center",
        gap: 12,
        textAlign: "left",
        padding: "12px 14px",
        background: t.bg,
        color: t.fg,
        border: `1.5px solid ${t.border}`,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <Ico sx={{ fontSize: 24, color: t.icon, flex: "0 0 auto" }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 16, fontWeight: 800 }}>{title}</span>
        <span
          style={{ display: "block", fontSize: 12.5, fontWeight: 600, opacity: 0.85, marginTop: 1 }}
        >
          {subtitle}
        </span>
      </span>
      <ChevronRightIcon sx={{ fontSize: 22, color: t.icon, opacity: 0.9, flex: "0 0 auto" }} />
    </button>
  );
}

Action.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  tone: PropTypes.oneOf(["confirm", "primary", "takeover", "danger"]).isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

Action.defaultProps = { disabled: false };

/* ================================== siden ================================= */
function VendorLink() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [note, setNote] = useState("");
  // Leveringsplanen er sammenslått: seks lass fyller ellers hele skjermen før
  // produsenten kommer til det de faktisk skal svare på.
  const [planOpen, setPlanOpen] = useState(false);

  // Tiden produsenten bekrefter (Utvikling | BMAx s. 2). Begge feltene er
  // påkrevd — backend avviser en bekreftelse uten dato og tid.
  const [pDate, setPDate] = useState("");
  const [pTime, setPTime] = useState("");
  const [confirmError, setConfirmError] = useState(null);

  // --- forespørselsmodus ---
  // Fastprisen: pris og vilkår er begge påkrevd. En pris uten forbehold kan vi
  // ikke sammenligne mot de andre, og den går videre til kunden som den er.
  const [priceOpen, setPriceOpen] = useState(false);
  const [price, setPrice] = useState("");
  const [priceIncVat, setPriceIncVat] = useState(false);
  const [terms, setTerms] = useState("");
  const [takeoverOpen, setTakeoverOpen] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [declineReqOpen, setDeclineReqOpen] = useState(false);
  // Egen feilkanal: en avvist fastpris skal ikke bytte ut hele siden med
  // «Lenken er ikke gyldig», som `error` gjør.
  const [reqError, setReqError] = useState(null);

  const post = useCallback(async (body) => {
    const res = await fetch(`${API_BASE}/connect`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return res.json();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await post({ action: "vendor_link_order", token });
      if (res?.ok && res.data) setData(res.data);
      else setError(res?.error === "invalid_token" ? "invalid" : "server");
    } catch (e) {
      setError("network");
    } finally {
      setLoading(false);
    }
  }, [post, token]);

  useEffect(() => {
    load();
  }, [load]);

  // Forhåndsutfyll bekreftelsen med kundens første ønske. Produsenten bekrefter
  // som oftest det kunden ba om, og skal slippe å taste det inn på nytt. Ved
  // «Så snart som mulig» / «Avtal senere» finnes det ikke noe ønske å fylle inn.
  useEffect(() => {
    const first = (data?.customer_wish || []).find((x) => x.date);
    if (!first) return;
    setPDate((prev) => prev || first.date);
    if (first.time) setPTime((prev) => prev || first.time);
  }, [data]);

  const respond = async (decision) => {
    if (decision === "accept" && (!pDate || !pTime)) {
      setConfirmError("Velg dato og tidspunkt dere kan levere før dere bekrefter.");
      return;
    }
    setConfirmError(null);
    setBusy(true);
    try {
      const res = await post({
        action: "vendor_link_respond",
        token,
        decision,
        note,
        // Bekreftelsen bærer tiden. Ved avslag er den uten betydning.
        ...(decision === "accept" ? { date: pDate, time: pTime } : {}),
      });
      if (res?.ok) {
        setDeclineOpen(false);
        await load();
      } else if (res?.error === "date_and_time_required") {
        setConfirmError("Velg en gyldig dato og et gyldig tidspunkt før dere bekrefter.");
      } else if (res?.error === "date_in_past") {
        setConfirmError("Datoen har vært. Velg en dato fra i dag og framover.");
      } else if (res?.error === "not_awaiting_vendor") {
        setConfirmError("Ordren venter ikke lenger på svar. Ta kontakt med Betongsentrum.");
      } else {
        setError("server");
      }
    } catch (e) {
      setError("network");
    } finally {
      setBusy(false);
    }
  };

  /* ------------------------ forespørsel: de tre svarene --------------------- */
  // Feilkoder fra vendorRequest.js, oversatt til noe en produsent kan handle på.
  const REQ_ERRORS = {
    invalid_price: "Skriv inn en gyldig pris.",
    terms_required: "Legg inn vilkårene for leveransen før du sender fastprisen.",
    terms_not_accepted: "Du må godta vilkårene for formidlede forespørsler.",
    already_taken_over: "En annen produsent overtok forespørselen først.",
    request_closed: "Denne forespørselen er avsluttet.",
    deadline_passed: "Svarfristen har gått ut. Ta kontakt med Betongsentrum.",
    invalid_token: "Lenken er ikke gyldig lenger.",
  };

  const sendRequest = async (body) => {
    setBusy(true);
    setReqError(null);
    try {
      const res = await post({ ...body, token });
      if (res?.ok) {
        // Svaret inneholder hele den nye visningen — inkludert kundeinfoen som
        // låses opp ved overtakelse — så vi slipper en ekstra rundtur.
        if (res.data) setData(res.data);
        else await load();
        setPriceOpen(false);
        setTakeoverOpen(false);
        setDeclineReqOpen(false);
        return true;
      }
      setReqError(REQ_ERRORS[res?.error] || "Noe gikk galt. Prøv igjen.");
      return false;
    } catch (e) {
      setReqError("Fikk ikke kontakt med Betongsentrum. Prøv igjen.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Fastprisen har vilkårsfeltet, og ved overtakelse snakker produsenten med
  // kunden selv. Notatet hører bare hjemme ved avslag, som begrunnelse.
  const submitPrice = () =>
    sendRequest({ action: "vendor_request_quote", price, price_inc_vat: priceIncVat, terms });

  const submitTakeover = () =>
    sendRequest({ action: "vendor_request_takeover", accept_terms: acceptTerms });

  const submitRequestDecline = () => sendRequest({ action: "vendor_request_decline", note });

  /* --------------------------------- states -------------------------------- */
  if (loading) {
    return (
      <div className="vl-page" style={{ display: "grid", placeItems: "center" }}>
        <p style={{ color: MUTED }}>Laster…</p>
      </div>
    );
  }

  if (error === "invalid" || (!data && error)) {
    return (
      <div className="vl-page">
        <div className="vl-wrap">
          <div className="vl-card" style={{ textAlign: "center", padding: 28 }}>
            <h2 style={{ fontSize: 19, margin: "0 0 8px" }}>Lenken er ikke gyldig</h2>
            <p style={{ color: MUTED, fontSize: 14, margin: 0 }}>
              {error === "invalid"
                ? "Denne leveranselenken finnes ikke, eller den er utdatert. Ta kontakt med Betongsentrum."
                : "Noe gikk galt. Prøv igjen om litt."}
            </p>
            {error !== "invalid" && (
              <button
                type="button"
                onClick={load}
                style={{ ...S.btn, marginTop: 18, background: BRAND, color: "#fff" }}
              >
                Prøv igjen
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const d = data;
  const isRequest = d.mode === "foresporsel";
  // I forespørselsmodus er «besvart» alt annet enn sendt: gitt pris, overtatt,
  // avslått eller lukket. Da forsvinner handlingene og svaret vises i stedet.
  const answered = isRequest ? d.state && d.state !== "sent" : d.responded;
  const accepted = d.response?.decision === "accept";
  const takenOver = isRequest && d.state === "taken_over";
  const priced = isRequest && (d.state === "priced" || d.state === "selected");
  const requestClosed = isRequest && (d.expired || ["not_selected", "cancelled"].includes(d.state));
  const feeText = `${Number(d.fee_amount || 0).toLocaleString("nb-NO")} kr eks. mva.`;
  // Bestilling: `can_respond` sier om backend tar imot svaret nå. Uten den fikk
  // produsenten skjemaet på en ordre som ikke ventet på svar, og både bekreft
  // og avslå endte i en feilmelding.
  const waitingElsewhere = !isRequest && !answered && d.can_respond === false;
  const canAct = isRequest ? !answered && !requestClosed : !answered && !waitingElsewhere;

  // Kartsøket bruker hele adressen; kortet viser gate og sted på hver sin linje.
  const mapsQuery = d.address_full || d.address;
  const mapsHref = mapsQuery
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}`
    : null;
  const hasCoords =
    Number.isFinite(Number(d.lat)) &&
    Number.isFinite(Number(d.lng)) &&
    !(Number(d.lat) === 1 && Number(d.lng) === 1);

  // Kundens ønskede tidspunkt(er), fra bestillingen. Lassene bærer den AVTALTE
  // tiden etter at produsenten har bekreftet, så de kan ikke brukes her.
  const requestedTimes = (() => {
    const byKey = new Map();
    (d.customer_wish || []).forEach((x) => {
      const key = `${x.date}|${x.time || ""}`;
      if (!byKey.has(key))
        byKey.set(key, { date: x.date, time: x.time || "", window: x.time_window || "" });
    });
    return [...byKey.values()].sort((a, b) =>
      `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`)
    );
  })();

  const timing = d.delivery_timing;
  const TIMING_TEXT = { asap: "Så snart som mulig", later: "Avtal senere" };

  /* ------------------------------- statusmerke ------------------------------ */
  const requestBadge = () => {
    if (takenOver) return ["Forespørselen er overtatt", `Formidlingsgebyr ${feeText}`];
    if (priced) return ["Fastpris sendt", "Betongsentrum sammenligner prisene."];
    if (d.state === "declined") return ["Forespørselen er lukket", "Dere har takket nei."];
    if (d.expired) return ["Forespørselen er lukket", "Svarfristen har gått ut."];
    if (requestClosed) return ["Forespørselen er lukket", "Den gikk til en annen produsent."];
    return ["Ny forespørsel", "Kundens kontaktinfo vises først etter overtakelse."];
  };
  const [badgeTitle, badgeBody] = isRequest ? requestBadge() : [];

  return (
    <div className="vl-page">
      <div className="vl-wrap vl-split">
        {/* Skjerm: to kolonner, det produsenten leser til venstre og det de
            svarer på til høyre (kundens skisse 28.09.2026). Telefon: én kolonne i
            samme rekkefølge som før — se .vl-split i style.css. */}
        <div className="vl-main">
          {/* -------------------------------- header ----------------------------- */}
          <div className="vl-card vl-o-head">
            <div className="vl-header">
              <div style={{ minWidth: 0 }}>
                <Head
                  icon={isRequest ? ArticleIcon : LocalShippingIcon}
                  color={isRequest ? AMBER : BRAND}
                >
                  {isRequest ? "Kundeforespørsel" : "Ordre"}
                  {/* «fra Betongsentrum» faller bort på telefon: den brakk
                      stikktittelen til to linjer, og avsenderen står i setningen
                      rett under uansett. */}
                  <span className="vl-eyebrow-tail"> fra Betongsentrum</span>
                </Head>
                <h1 className="vl-title">
                  {isRequest ? `Forespørsel #${d.request_number}` : `Ordre #${d.order_number}`}
                </h1>
                {/* Andre setning faller bort på telefon: den beskriver knappene
                    som står lenger ned uansett. */}
                <p className="vl-sub">
                  {isRequest
                    ? "Betongsentrum har mottatt en kundeforespørsel i deres område."
                    : "Kunden har bestilt og betalt gjennom Betongsentrum."}
                  <span className="vl-sub-tail">
                    {isRequest
                      ? " Dere kan gi fastpris eller overta kunden."
                      : " Vennligst bekreft ordren og leveringstid."}
                  </span>
                </p>
              </div>

              {/* Statusmerket øverst til høyre i skissen. */}
              {isRequest ? (
                <div className="vl-badge" style={{ background: "#fff8e6", borderColor: "#f3d9ae" }}>
                  <InfoIcon sx={{ fontSize: 19, color: "#b26a00", flex: "0 0 auto" }} />
                  <div>
                    <p style={{ fontSize: 13.5, fontWeight: 800, margin: 0, color: "#8a5200" }}>
                      {badgeTitle}
                    </p>
                    {/* Detaljlinjen faller bort på telefon — den er det største
                        enkeltelementet i topplokket, og innholdet står igjen
                        både i kundeinformasjonskortet og i «Hva skjer videre?».
                        Tilstanden over er den som må være synlig med én gang. */}
                    <p className="vl-badge-body">{badgeBody}</p>
                  </div>
                </div>
              ) : (
                <div
                  className="vl-badge"
                  style={{
                    // Grønt er forbeholdt bekreftede tilstander; en ordre som
                    // ikke venter på produsenten er nøytral.
                    background: waitingElsewhere ? TINT : answered && !accepted ? "#fdecea" : "#e7f6ed",
                    borderColor: waitingElsewhere ? "#dbe7f7" : answered && !accepted ? "#f5c6c2" : "#b7e3c8",
                  }}
                >
                  {waitingElsewhere ? (
                    <InfoIcon sx={{ fontSize: 19, color: MUTED, flex: "0 0 auto" }} />
                  ) : answered && !accepted ? (
                    <CloseIcon sx={{ fontSize: 19, color: RED, flex: "0 0 auto" }} />
                  ) : (
                    <CheckCircleIcon sx={{ fontSize: 19, color: GREEN, flex: "0 0 auto" }} />
                  )}
                  <p
                    style={{
                      fontSize: 13.5,
                      fontWeight: 800,
                      margin: 0,
                      color: waitingElsewhere ? MUTED : answered && !accepted ? RED : GREEN,
                    }}
                  >
                    {waitingElsewhere
                      ? "Venter på Betongsentrum."
                      : !answered
                      ? "Ordren er klar til levering."
                      : accepted
                      ? "Leveransen er bekreftet."
                      : "Leveransen er avslått."}
                  </p>
                </div>
              )}
            </div>

            <div className="vl-meta">
              {/* What the producer writes on the delivery note — men på telefon er
                  det ord for ord det samme som overskriften rett over, så det
                  skjules der. */}
              <div className="vl-meta-mark">
                <Row
                  label="Merket"
                  value={isRequest ? `Forespørsel #${d.request_number}` : `Ordre #${d.order_number}`}
                />
              </div>
              <div className="vl-meta-received">
                <Row label="Mottatt" value={fmtReceived(d.created_at)} />
              </div>
              {isRequest && d.deadline && (
                <div className="vl-meta-deadline">
                  <Row label="Svarfrist" value={fmtReceived(d.deadline)} />
                </div>
              )}
            </div>
          </div>

          {/* --------------------------- leveringsdetaljer ----------------------- */}
          <div className="vl-card" style={{ background: TINT, borderColor: "#dbe7f7" }}>
            <Head>Leveringsdetaljer</Head>
            <div className="vl-details">
              <Field
                icon={ViewInArIcon}
                label="Volum"
                value={d.total_volume ? `${d.total_volume} m³` : null}
              />
              <Field icon={LocalShippingIcon} label="Biltype" value={d.vehicle_type} />
              <Field icon={ScienceIcon} label="Betongkvalitet / resept" value={d.concrete_recipe} />
              <Field icon={AccessTimeIcon} label="Estimert lossetid (anslått)" value={d.unload_time} />
              <Field icon={AdjustIcon} label="Type støp" value={d.cast_type} />
              <Field
                icon={WaterDropIcon}
                label="Tilsetning"
                value={d.additives?.length ? d.additives.join(", ") : "Ingen"}
              />
            </div>
          </div>

          {/* ---------------------------- notat fra kunden ----------------------- */}
          {d.customer_note && (
            <div className="vl-card">
              <Head icon={ChatBubbleIcon}>Notat fra kunden</Head>
              <div
                style={{
                  border: `1px solid ${LINE}`,
                  borderRadius: 12,
                  padding: "12px 14px",
                  fontSize: 15,
                  color: INK,
                }}
              >
                {d.customer_note}
              </div>
            </div>
          )}

          {/* --------------------- vedlegg fra Betongsentrum --------------------- */}
          {/* Kartutsnitt, Street View-bilder og dokumentasjon vi har fått separat.
              Uten dem må produsenten prise på adressen alene, som er nettopp det
              som gjør at store jobber ikke kan prises automatisk. */}
          {d.attachments?.length > 0 && (
            <div className="vl-card">
              <Head icon={AttachFileIcon}>Vedlegg fra Betongsentrum</Head>
              <ul className="vl-attachments">
                {d.attachments.map((a) => (
                  <li key={a.id}>
                    <a href={a.url} target="_blank" rel="noopener noreferrer">
                      {a.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* ----------------------- leveringsplan (sammenslått) ----------------- */}
          {/* Klikkes opp. Med seks lass dytter den ellers alt man skal svare på
              langt ned, og innholdet er sjelden det avgjørende. */}
          {d.deliveries?.length > 0 && (
            <div className="vl-card" style={{ padding: 0, overflow: "hidden" }}>
              <button
                type="button"
                onClick={() => setPlanOpen((v) => !v)}
                aria-expanded={planOpen}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  background: "none",
                  border: "none",
                  padding: 18,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                <span
                  style={{
                    flex: 1,
                    textAlign: "left",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: ".08em",
                    textTransform: "uppercase",
                    color: BRAND,
                  }}
                >
                  Leveringsplan – {d.deliveries.length} lass
                </span>
                <span style={{ fontSize: 13, color: MUTED, fontWeight: 600 }}>
                  {planOpen ? "Skjul" : "Vis"}
                </span>
                <ExpandMoreIcon
                  sx={{
                    fontSize: 22,
                    color: MUTED,
                    transform: planOpen ? "rotate(180deg)" : "none",
                    transition: "transform .18s",
                  }}
                />
              </button>

              {planOpen && (
                <div style={{ padding: "0 18px 18px" }}>
                  <div className="vl-loads">
                    {d.deliveries.map((x) => (
                      <div
                        key={x.index}
                        style={{ border: `1px solid ${LINE}`, borderRadius: 12, padding: 12 }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                          <span
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: 8,
                              background: BRAND,
                              color: "#fff",
                              fontSize: 12.5,
                              fontWeight: 700,
                              display: "grid",
                              placeItems: "center",
                            }}
                          >
                            {x.index}
                          </span>
                          <strong style={{ fontSize: 14 }}>
                            Lass {x.index} av {d.deliveries.length}
                          </strong>
                        </div>
                        {/* No date/time per lass: the delivery time is agreed once for
                            the whole order in the «Leveringstid» card below. */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                          <Row label="Volum" value={x.volume ? `${x.volume} m³` : null} />
                          <Row label="Biltype" value={x.vehicle_type} />
                        </div>
                        <div style={{ height: 12 }} />
                        <Row label="Betong" value={x.concrete_type} />
                        <div style={{ height: 12 }} />
                        <Row
                          label="Tilsetning"
                          value={x.additives?.length ? x.additives.join(", ") : "Ingen"}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Wording matches what the system actually does. */}
          <div className="vl-card vl-o-next" style={{ background: "#eef3f9", borderColor: "#dbe4ef" }}>
            <Head icon={InfoIcon}>Hva skjer videre?</Head>
            {isRequest ? (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, lineHeight: 1.65, color: INK }}>
                <li>
                  <strong>Gir dere fastpris</strong>, sammenligner Betongsentrum prisen med de andre
                  produsentene som har fått forespørselen, og gir dere svar.
                </li>
                <li>
                  <strong>Tar dere over forespørselen</strong>, får dere kundens kontaktinformasjon med én
                  gang og håndterer kunden, prisen og faktureringen selv. Ingen andre produsenter får
                  forespørselen. Betongsentrum fakturerer et formidlingsgebyr på {feeText}
                </li>
                <li>
                  <strong>Avslår dere</strong>, går forespørselen videre til de andre produsentene.
                </li>
              </ul>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, lineHeight: 1.65, color: INK }}>
                <li>
                  <strong>Bekrefter dere</strong>, settes ordren til «Bekreftet av produsent».
                  Betongsentrum kontrollerer tidspunktet og sender det videre til kunden.
                </li>
                <li>
                  <strong>Avslår dere</strong>, settes ordren til «Avvist av produsent» og
                  Betongsentrum får beskjed om å finne en annen.
                </li>
                <li>
                  <strong>Tidspunktet dere oppgir</strong> blir den avtalte leveringstiden. Kundens
                  ønske over står uendret, så Betongsentrum ser om det avviker.
                </li>
              </ul>
            )}
          </div>
        </div>

        <div className="vl-side">
          {/* ------------------------------ byggeplass --------------------------- */}
          <div className="vl-card vl-o-site">
            <Head icon={LocationOnIcon}>Byggeplass</Head>
            <div className="vl-site">
              {/* Skriftstørrelsene ligger i CSS, ikke her: adressen deler bredden
                  med kartet også på telefon, og må krympe der. */}
              <div className="vl-site-addr">
                <p className="vl-site-street">{d.address || "—"}</p>
                {(d.postcode || d.town) && (
                  <p className="vl-site-town">{[d.postcode, d.town].filter(Boolean).join(" ")}</p>
                )}
                {d.distance_km != null && (
                  <p className="vl-site-dist">
                    {Number(d.distance_km).toLocaleString("nb-NO", { maximumFractionDigits: 1 })} km fra
                    fabrikk
                  </p>
                )}
              </div>

              <div>
                {hasCoords ? (
                  <div className="vl-map">
                    {/* OSM-innbyggingen har sin egen bunnlinje med «Rapporter et
                        problem», «Make a Donation» og «Website and API terms». På
                        et kart som er 118 px høyt på telefon spiser den en femtedel
                        av flaten. Rammen er derfor høyere enn beholderen, så den
                        linjen havner utenfor og klippes bort av overflow: hidden.
                        Krediteringen er IKKE valgfri — kartdataene er ODbL, som
                        krever «© OpenStreetMap contributors» — så den tegnes på
                        nytt under, kompakt og med lenke til lisensen. */}
                    <iframe
                      title="Leveringskart"
                      scrolling="no"
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${
                        Number(d.lng) - 0.008
                      },${Number(d.lat) - 0.004},${Number(d.lng) + 0.008},${
                        Number(d.lat) + 0.004
                      }&layer=mapnik&marker=${d.lat},${d.lng}`}
                      className="vl-map-frame"
                    />
                    <a
                      className="vl-map-credit"
                      href="https://www.openstreetmap.org/copyright"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      © OpenStreetMap
                    </a>
                    {mapsHref && (
                      <a
                        className="vl-map-btn"
                        href={mapsHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Vis større kart"
                      >
                        <OpenInFullIcon />
                        {/* Teksten faller bort på telefon: kartet er bare ~150 px
                            bredt der, og knappen med tekst dekket 73 % av det —
                            inkludert markøren den skulle hjelpe deg å se. */}
                        <span className="vl-map-btn-text">Vis større kart</span>
                      </a>
                    )}
                  </div>
                ) : (
                  mapsHref && (
                    <a
                      href={mapsHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "block",
                        textAlign: "center",
                        padding: "12px 14px",
                        border: `1px solid ${BRAND}`,
                        borderRadius: 12,
                        color: BRAND,
                        fontWeight: 700,
                        fontSize: 14,
                        textDecoration: "none",
                      }}
                    >
                      Åpne veibeskrivelse
                    </a>
                  )
                )}
              </div>
            </div>
          </div>

          {/* --------------------- kunden ønsker levering ------------------------ */}
          {/* Utvikling | BMAx s. 2: kundens ønske og Betongsentrums notat i samme
              gule boks, rett etter leveringsplanen. Begge produsentsidene — på en
              forespørsel er det dette produsenten priser etter. Kundens eget notat
              står i sitt eget kort lenger opp og gjentas ikke her. */}
          <div className="vl-card" style={{ background: "#fffaf0", borderColor: "#f5d9a0" }}>
            <h2 style={{ fontSize: 20, fontWeight: 800, margin: "0 0 14px", color: INK }}>
              Kunden ønsker levering
            </h2>

            <div className="vl-wish">
              {timing !== "chosen" || !requestedTimes.length ? (
                <div className="vl-wish-row">
                  <span className="vl-wish-item">
                    <CalendarMonthIcon sx={{ fontSize: 26, color: BRAND, flex: "0 0 auto" }} />
                    <span>
                      <span style={{ display: "block", fontSize: 18, fontWeight: 800 }}>
                        {TIMING_TEXT[timing] || "Ikke oppgitt"}
                      </span>
                      <span style={{ display: "block", fontSize: 13, color: MUTED }}>
                        {timing === "asap"
                          ? "Kunden vil ha levering så tidlig som mulig."
                          : "Kunden har ikke oppgitt dag. Tidspunktet avtales."}
                      </span>
                    </span>
                  </span>
                </div>
              ) : (
                requestedTimes.map((t) => (
                  <div key={`${t.date}|${t.time}`} className="vl-wish-row">
                    <span className="vl-wish-item">
                      <CalendarMonthIcon sx={{ fontSize: 26, color: BRAND, flex: "0 0 auto" }} />
                      <span>
                        <span style={{ display: "block", fontSize: 18, fontWeight: 800 }}>
                          {fmtLongDate(t.date)}
                        </span>
                        <span style={{ display: "block", fontSize: 13, color: MUTED }}>
                          Dette er kundens ønskede leveringstidspunkt.
                        </span>
                      </span>
                    </span>
                    <span className="vl-wish-item">
                      <AccessTimeIcon sx={{ fontSize: 26, color: BRAND, flex: "0 0 auto" }} />
                      <span style={{ fontSize: 18, fontWeight: 800, whiteSpace: "nowrap" }}>
                        {t.window || (t.time ? `kl. ${t.time}` : "tid ikke satt")}
                      </span>
                    </span>
                  </div>
                ))
              )}
            </div>

            {isRequest && (
              <p style={{ fontSize: 13, color: MUTED, margin: "12px 0 0", lineHeight: 1.45 }}>
                Ta forbehold om tidspunkt i vilkårene deres hvis det ikke passer.
              </p>
            )}

            {d.bmax_note && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid #f0dfc0` }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <DescriptionIcon sx={{ fontSize: 22, color: BRAND, flex: "0 0 auto", mt: "1px" }} />
                  <span>
                    <span
                      style={{
                        display: "block",
                        fontSize: 12,
                        fontWeight: 800,
                        letterSpacing: ".06em",
                        textTransform: "uppercase",
                        color: BRAND,
                      }}
                    >
                      Notat fra Betongsentrum
                    </span>
                    <span
                      style={{
                        display: "block",
                        fontSize: 15.5,
                        color: INK,
                        marginTop: 2,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {d.bmax_note}
                    </span>
                  </span>
                </div>
              </div>
            )}
          </div>

          {waitingElsewhere && (
            <div className="vl-card" style={{ background: "#eef3f9", borderColor: "#dbe4ef" }}>
              <p style={{ fontSize: 14, fontWeight: 700, margin: "0 0 4px" }}>
                Ordren venter ikke på svar fra dere nå
              </p>
              <p style={{ color: MUTED, fontSize: 13.5, margin: 0 }}>
                Betongsentrum har ikke sendt den til bekreftelse ennå, eller den er endret
                siden lenken ble sendt. Ta kontakt med Betongsentrum.
              </p>
            </div>
          )}

          {/* ------------------------ bekreft leveringstid ----------------------- */}
          {!isRequest && canAct && (
            <div className="vl-card" style={{ padding: 0, overflow: "hidden", borderColor: "#b7e3c8" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  background: "#eaf7ef",
                  padding: "14px 18px",
                }}
              >
                <CalendarMonthIcon sx={{ fontSize: 22, color: GREEN }} />
                <span
                  style={{
                    fontSize: 17,
                    fontWeight: 800,
                    letterSpacing: ".02em",
                    textTransform: "uppercase",
                    color: GREEN,
                  }}
                >
                  Bekreft leveringstid
                </span>
              </div>

              <div style={{ padding: 18 }}>
                <p style={{ fontSize: 14.5, color: INK, margin: "0 0 16px" }}>
                  Velg dato og tidspunkt dere kan levere. Dette sendes til kunden.
                </p>

                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                    <p style={{ ...S.label, display: "flex", alignItems: "center", gap: 6 }}>
                      <CalendarMonthIcon sx={{ fontSize: 15, color: BRAND }} />
                      Dato for levering
                    </p>
                    <input
                      type="date"
                      min={new Date().toLocaleDateString("sv-SE")}
                      value={pDate}
                      onChange={(e) => setPDate(e.target.value)}
                      style={{ ...S.input, marginTop: 4 }}
                    />
                  </div>
                  <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                    <p style={{ ...S.label, display: "flex", alignItems: "center", gap: 6 }}>
                      <AccessTimeIcon sx={{ fontSize: 15, color: BRAND }} />
                      Tidspunkt
                    </p>
                    <input
                      type="time"
                      step="900"
                      value={pTime}
                      onChange={(e) => setPTime(e.target.value)}
                      style={{ ...S.input, marginTop: 4 }}
                    />
                  </div>
                </div>

                <div
                  style={{
                    background: "#f6f8fb",
                    border: `1px solid ${LINE}`,
                    borderRadius: 12,
                    padding: 14,
                    marginTop: 16,
                  }}
                >
                  <p style={{ ...S.label, display: "flex", alignItems: "center", gap: 6 }}>
                    <ChatBubbleIcon sx={{ fontSize: 14, color: INK }} />
                    Notat til Betongsentrum <span style={{ opacity: 0.7 }}>(valgfritt)</span>
                  </p>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value.slice(0, 250))}
                    placeholder="Skriv eventuelt notat …"
                    rows={2}
                    style={{ ...S.input, minHeight: 64, resize: "none", paddingTop: 12, marginTop: 6 }}
                  />
                  <p style={{ ...S.label, textAlign: "right", marginTop: 6 }}>
                    {note.length} / 250 tegn
                  </p>
                </div>

                {confirmError && (
                  <p
                    style={{
                      fontSize: 13.5,
                      color: RED,
                      background: "#fdecea",
                      border: "1px solid #f5c6c2",
                      borderRadius: 12,
                      padding: "12px 14px",
                      margin: "16px 0 0",
                    }}
                  >
                    {confirmError}
                  </p>
                )}

                {declineOpen ? (
                  <div style={{ marginTop: 16 }}>
                    <p style={{ fontSize: 14, color: INK, margin: "0 0 14px", lineHeight: 1.5 }}>
                      Avslår dere, går ordren tilbake til Betongsentrum for ny produsent. Dette kan
                      ikke angres.
                    </p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => respond("decline")}
                        style={{ ...S.btn, background: RED, color: "#fff", opacity: busy ? 0.6 : 1 }}
                      >
                        {busy ? "Sender…" : "Bekreft avslag"}
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setDeclineOpen(false)}
                        style={{
                          ...S.btn,
                          minHeight: 46,
                          background: "#fff",
                          color: MUTED,
                          border: `1px solid ${LINE}`,
                        }}
                      >
                        Avbryt
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="vl-confirm">
                    <Action
                      icon={CheckIcon}
                      tone="confirm"
                      title={busy ? "Sender…" : "Bekreft ordre"}
                      subtitle="Jeg kan levere denne bestillingen"
                      disabled={busy}
                      onClick={() => respond("accept")}
                    />
                    <Action
                      icon={CloseIcon}
                      tone="danger"
                      title="Avslå ordre"
                      subtitle="Jeg kan ikke levere denne ordren"
                      disabled={busy}
                      onClick={() => setDeclineOpen(true)}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ------------------------------ deres svar --------------------------- */}
          {/* Etter svaret forsvant skjemaet, og det eneste produsenten så var
              merket øverst. Her står det de faktisk sendte. */}
          {!isRequest && answered && d.response && (
            <div
              className="vl-card"
              style={{ borderColor: accepted ? "#b7e3c8" : "#f5c6c2" }}
            >
              <Head icon={accepted ? CheckCircleIcon : CloseIcon} color={accepted ? GREEN : RED}>
                {accepted ? "Dere har bekreftet" : "Dere har avslått"}
              </Head>
              {accepted && d.response.confirmed_date && (
                <div className="vl-wish-row">
                  <span className="vl-wish-item">
                    <CalendarMonthIcon sx={{ fontSize: 24, color: GREEN, flex: "0 0 auto" }} />
                    <span style={{ fontSize: 17, fontWeight: 800 }}>
                      {fmtLongDate(d.response.confirmed_date)}
                    </span>
                  </span>
                  {d.response.confirmed_time && (
                    <span className="vl-wish-item">
                      <AccessTimeIcon sx={{ fontSize: 24, color: GREEN, flex: "0 0 auto" }} />
                      <span style={{ fontSize: 17, fontWeight: 800 }}>
                        kl. {d.response.confirmed_time}
                      </span>
                    </span>
                  )}
                </div>
              )}
              {d.response.vendor_note && (
                <p style={{ fontSize: 14.5, color: INK, margin: "12px 0 0", whiteSpace: "pre-wrap" }}>
                  <strong>Notat til Betongsentrum:</strong> {d.response.vendor_note}
                </p>
              )}
              {d.response.at && (
                <p style={{ fontSize: 12.5, color: MUTED, margin: "10px 0 0" }}>
                  Sendt {fmtReceived(d.response.at)}
                </p>
              )}
            </div>
          )}

          {/* ----------------------- forespørsel: deres fastpris ----------------- */}
          {isRequest && priced && d.quote?.price != null && (
            <div className="vl-card">
              <Head icon={LocalOfferIcon}>Deres fastpris</Head>
              <p style={{ ...S.value, fontSize: 20, marginTop: 0 }}>
                {Number(d.quote.price).toLocaleString("nb-NO")} kr{" "}
                <span style={{ fontSize: 13, color: MUTED, fontWeight: 600 }}>
                  {d.quote.price_inc_vat ? "inkl. mva." : "eks. mva."}
                </span>
              </p>
              {d.quote.terms && (
                <p style={{ fontSize: 14, color: INK, margin: "10px 0 0", whiteSpace: "pre-wrap" }}>
                  <strong>Vilkår:</strong> {d.quote.terms}
                </p>
              )}
              <p style={{ fontSize: 13, color: MUTED, margin: "10px 0 0", lineHeight: 1.5 }}>
                Betongsentrum har mottatt fastprisen. Godtar kunden tilbudet og Betongsentrum velger
                dere, får dere en offisiell bestilling.
              </p>
            </div>
          )}

          {/* -------------------------------- handlinger ------------------------- */}
          {isRequest && canAct && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 14 }}>
              {reqError && (
                <p
                  style={{
                    fontSize: 13.5,
                    color: RED,
                    background: "#fdecea",
                    border: "1px solid #f5c6c2",
                    borderRadius: 12,
                    padding: "12px 14px",
                    margin: 0,
                  }}
                >
                  {reqError}
                </p>
              )}

              {/* ------------ forespørsel: fastpris / overta / avslå ------------ */}
              {isRequest && priceOpen && (
                <div className="vl-card" style={{ marginBottom: 0 }}>
                  <Head icon={LocalOfferIcon}>Gi fastpris</Head>
                  <p style={S.label}>Fastpris for hele leveransen</p>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="F.eks. 24 500"
                      style={{ ...S.input, flex: 1 }}
                    />
                    <span style={{ fontSize: 15, fontWeight: 700, color: MUTED }}>kr</span>
                  </div>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      margin: "10px 0 16px",
                      fontSize: 13.5,
                      color: MUTED,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={priceIncVat}
                      onChange={(e) => setPriceIncVat(e.target.checked)}
                      style={{ width: 18, height: 18 }}
                    />
                    Prisen er inkl. mva.
                  </label>

                  <p style={S.label}>Vilkår for leveransen (påkrevd)</p>
                  <textarea
                    value={terms}
                    onChange={(e) => setTerms(e.target.value.slice(0, 2000))}
                    rows={4}
                    placeholder="Hva er inkludert, forbehold om tidspunkt, ventetid, adkomst, gyldighet på prisen …"
                    style={{ ...S.input, minHeight: 110, resize: "vertical", paddingTop: 12 }}
                  />
                  <p style={{ ...S.label, textAlign: "right", marginTop: 4 }}>
                    {terms.length} / 2000 tegn
                  </p>

                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
                    <button
                      type="button"
                      disabled={busy || !price.trim() || !terms.trim()}
                      onClick={submitPrice}
                      style={{
                        ...S.btn,
                        background: BRAND,
                        color: "#fff",
                        opacity: busy || !price.trim() || !terms.trim() ? 0.6 : 1,
                      }}
                    >
                      {busy ? "Sender…" : "Send fastpris"}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setPriceOpen(false);
                        setReqError(null);
                      }}
                      style={{
                        ...S.btn,
                        minHeight: 46,
                        background: "#fff",
                        color: MUTED,
                        border: `1px solid ${LINE}`,
                      }}
                    >
                      Avbryt
                    </button>
                  </div>
                </div>
              )}

              {isRequest && !priceOpen && declineReqOpen && (
                <div className="vl-card" style={{ marginBottom: 0 }}>
                  <Head icon={CloseIcon} color={RED}>
                    Avslå forespørsel
                  </Head>
                  <p style={{ fontSize: 14, color: INK, margin: "0 0 14px", lineHeight: 1.5 }}>
                    Avslår dere, går forespørselen videre uten dere. Dette kan ikke angres.
                  </p>
                  <p style={S.label}>Begrunnelse til Betongsentrum (valgfritt)</p>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value.slice(0, 500))}
                    placeholder="F.eks. ikke kapasitet den dagen, for langt unna …"
                    rows={2}
                    style={{ ...S.input, minHeight: 64, resize: "none", paddingTop: 12 }}
                  />
                  <p style={{ ...S.label, textAlign: "right", margin: "6px 0 14px" }}>
                    {note.length} / 500 tegn
                  </p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={submitRequestDecline}
                      style={{ ...S.btn, background: RED, color: "#fff", opacity: busy ? 0.6 : 1 }}
                    >
                      {busy ? "Sender…" : "Bekreft avslag"}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setDeclineReqOpen(false);
                        setReqError(null);
                      }}
                      style={{
                        ...S.btn,
                        minHeight: 46,
                        background: "#fff",
                        color: MUTED,
                        border: `1px solid ${LINE}`,
                      }}
                    >
                      Avbryt
                    </button>
                  </div>
                </div>
              )}

              {isRequest && !priceOpen && !declineReqOpen && (
                <>
                  <Action
                    icon={LocalOfferIcon}
                    tone="primary"
                    title="Gi fastpris"
                    subtitle="Vi kan levere til en garantert fastpris"
                    disabled={busy}
                    onClick={() => {
                      setReqError(null);
                      setPriceOpen(true);
                    }}
                  />
                  <Action
                    icon={PersonIcon}
                    tone="takeover"
                    title="Ta over forespørsel"
                    subtitle="Ta over kunden og følg den opp direkte."
                    disabled={busy}
                    onClick={() => {
                      setReqError(null);
                      setAcceptTerms(false);
                      setTakeoverOpen(true);
                    }}
                  />
                  <Action
                    icon={CloseIcon}
                    tone="danger"
                    title="Avslå forespørsel"
                    subtitle="Vi ønsker ikke denne forespørselen"
                    disabled={busy}
                    onClick={() => {
                      setReqError(null);
                      setDeclineReqOpen(true);
                    }}
                  />
                </>
              )}

            </div>
          )}

          {/* ---------------------------- kundeinformasjon ----------------------- */}
          <div className="vl-card">
            <Head icon={isRequest ? PersonIcon : ApartmentIcon}>Kundeinformasjon</Head>

            {/* BESTILLING: produsentens motpart er Betongsentrum, ikke sluttkunden.
                Betongsentrum har solgt jobben og eier kundedialogen. */}
            {!isRequest && (
              <>
                <p style={{ ...S.value, fontSize: 16, marginTop: 0 }}>{d.bmax_contact?.company}</p>
                {d.bmax_contact?.person && (
                  <p style={{ fontSize: 13.5, color: MUTED, margin: "3px 0 0" }}>
                    Din kontaktperson: {d.bmax_contact.person}
                  </p>
                )}
                <ContactLinks phone={d.bmax_contact?.phone} email={d.bmax_contact?.email} />
              </>
            )}

            {/* FORESPØRSEL: låst til forespørselen er overtatt. Feltene finnes ikke
                i nyttelasten før da, så det er ingenting å lese ut av
                nettverksfanen heller. */}
            {isRequest && d.customer_unlocked && d.customer && (
              <>
                <p style={{ ...S.value, fontSize: 16, marginTop: 0 }}>{d.customer.name || "—"}</p>
                {d.customer.company && (
                  <p style={{ fontSize: 13.5, color: MUTED, margin: "3px 0 0" }}>{d.customer.company}</p>
                )}
                <ContactLinks phone={d.customer.phone} email={d.customer.email} />
                <p style={{ fontSize: 13, color: MUTED, margin: "14px 0 0", lineHeight: 1.5 }}>
                  Dere håndterer kunden direkte herfra, og står ansvarlig for pris, avtale, levering og
                  fakturering.
                </p>
              </>
            )}

            {isRequest && !(d.customer_unlocked && d.customer) && (
              <p style={{ fontSize: 14.5, color: MUTED, margin: 0, lineHeight: 1.55 }}>
                Kundeinformasjon opplyses ved overtatt forespørsel.
              </p>
            )}
          </div>
        </div>

        <p className="vl-foot" style={{ fontSize: 11.5, color: MUTED, textAlign: "center", margin: "18px 0 0" }}>
          {takenOver
            ? "Dere har overtatt forespørselen og håndterer kunden direkte. Spørsmål om formidlingsgebyret? Ta kontakt med Betongsentrum AS."
            : "Betongsentrum AS håndterer all kundedialog. Spørsmål? Ta kontakt med oss, ikke kunden."}
        </p>
      </div>

      {/* ------------------------------ overta-modal -------------------------- */}
      {/* Overtakelsen er den ene handlingen på siden som koster penger og ikke kan
          angres — den får derfor sin egen bekreftelse med gebyret i klartekst og
          et avkryssingsfelt som må hukes av. Serveren krever accept_terms === true
          uansett hva UI-et gjør. */}
      {takeoverOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Ta over forespørsel"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 60,
            background: "rgba(16,24,40,.55)",
            display: "grid",
            placeItems: "center",
            padding: 16,
          }}
          onClick={() => !busy && setTakeoverOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 18,
              padding: 22,
              width: "100%",
              maxWidth: 440,
              maxHeight: "88vh",
              overflowY: "auto",
              boxShadow: "0 18px 48px rgba(16,24,40,.28)",
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
              <PersonIcon sx={{ fontSize: 26, color: BRAND, flex: "0 0 auto" }} />
              <h2 style={{ fontSize: 19, fontWeight: 800, margin: 0, flex: 1 }}>Ta over forespørsel</h2>
              <button
                type="button"
                aria-label="Lukk"
                disabled={busy}
                onClick={() => setTakeoverOpen(false)}
                style={{
                  border: "none",
                  background: "none",
                  lineHeight: 1,
                  color: MUTED,
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                <CloseIcon sx={{ fontSize: 22 }} />
              </button>
            </div>

            <p style={{ fontSize: 14, color: MUTED, margin: "10px 0 0", lineHeight: 1.6 }}>
              Dere overtar forespørselen og håndterer kunden og leveransen direkte. Ved overtakelse får
              dere tilgang til kundens kontaktinformasjon, og står selv ansvarlig for pris, avtale,
              levering og fakturering.
            </p>

            <div
              style={{
                background: "#eef3f9",
                border: "1px solid #dbe4ef",
                borderRadius: 12,
                padding: "12px 14px",
                margin: "16px 0",
              }}
            >
              <p style={{ fontSize: 14.5, fontWeight: 800, margin: 0 }}>Formidlingsgebyr: {feeText}</p>
            </div>

            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                fontSize: 13.5,
                lineHeight: 1.5,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                style={{ width: 18, height: 18, marginTop: 1, flex: "0 0 auto" }}
              />
              <span>Jeg aksepterer BMAXs vilkår for formidlede forespørsler.</span>
            </label>

            {reqError && (
              <p
                style={{
                  fontSize: 13.5,
                  color: RED,
                  background: "#fdecea",
                  border: "1px solid #f5c6c2",
                  borderRadius: 10,
                  padding: "10px 12px",
                  margin: "14px 0 0",
                }}
              >
                {reqError}
              </p>
            )}

            <button
              type="button"
              disabled={busy || !acceptTerms}
              onClick={submitTakeover}
              style={{
                ...S.btn,
                marginTop: 16,
                background: BRAND,
                color: "#fff",
                opacity: busy || !acceptTerms ? 0.6 : 1,
              }}
            >
              {busy ? "Sender…" : `Ta over forespørsel – ${feeText.replace(" eks. mva.", "")}`}
            </button>

            <p style={{ fontSize: 12, color: MUTED, textAlign: "center", margin: "12px 0 0" }}>
              Kundens kontaktinformasjon vises etter bekreftelse.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default VendorLink;
